import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Ticket, Loader2, CheckCircle2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import { getPlanLimits } from "@/services/subscriptionService";

export default function CouponRedeemField() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  const handleRedeem = async () => {
    if (!user || !code.trim()) return;

    setIsRedeeming(true);
    try {
      const trimmedCode = code.trim().toUpperCase();

      // 1. Find coupon
      const { data: coupon, error: findErr } = await supabase
        .from("coupons")
        .select("*")
        .eq("code", trimmedCode)
        .eq("is_active", true)
        .maybeSingle();

      if (findErr || !coupon) {
        toast({ title: "Cupom inválido", description: "Este código de cupom não existe ou está inativo.", variant: "destructive" });
        return;
      }

      // Check expiry
      if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) {
        toast({ title: "Cupom expirado", description: "Este cupom já expirou.", variant: "destructive" });
        return;
      }

      // Check max uses
      if ((coupon.current_uses ?? 0) >= (coupon.max_uses ?? 0)) {
        toast({ title: "Cupom esgotado", description: "Este cupom já atingiu o limite de usos.", variant: "destructive" });
        return;
      }

      // Check if user already redeemed
      const { data: existing } = await supabase
        .from("coupon_redemptions")
        .select("id")
        .eq("coupon_id", coupon.id)
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        toast({ title: "Já utilizado", description: "Você já resgatou este cupom.", variant: "destructive" });
        return;
      }

      // 2. Apply coupon based on type
      if (coupon.coupon_type === "plan") {
        await applyPlanCoupon(coupon);
      } else if (coupon.coupon_type === "credits") {
        await applyCreditsCoupon(coupon);
      } else if (coupon.coupon_type === "storage") {
        await applyStorageCoupon(coupon);
      }

      // 3. Record redemption
      const { error: redemptionErr } = await supabase.from("coupon_redemptions").insert({
        coupon_id: coupon.id,
        user_id: user.id,
      });
      if (redemptionErr) throw new Error("Erro ao registrar resgate: " + redemptionErr.message);

      // 4. Increment uses
      const { error: usesErr } = await supabase
        .from("coupons")
        .update({ current_uses: (coupon.current_uses ?? 0) + 1 })
        .eq("id", coupon.id);
      if (usesErr) throw new Error("Erro ao atualizar usos: " + usesErr.message);

      // ✅ Tudo deu certo — agora sim confetti e toast
      setRedeemed(true);
      setCode("");

      confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { y: 0.5 } }), 300);
      queryClient.invalidateQueries({ queryKey: ["subscription"] });

      toast({ 
        title: "🎉 Cupom resgatado com sucesso!", 
        description: coupon.coupon_type === "plan" 
          ? `Plano ${coupon.plan_type} ativado por ${coupon.plan_duration_days} dias!`
          : coupon.coupon_type === "credits"
          ? `${formatCredits(coupon.credits_amount ?? 0)} créditos adicionados!`
          : `${formatBytes(coupon.storage_amount ?? 0)} de armazenamento adicionados!`
      });

      setTimeout(() => setRedeemed(false), 3000);
    } catch (err: any) {
      console.error("Erro ao resgatar cupom:", err);
      toast({ title: "Erro", description: err?.message || "Erro ao resgatar cupom. Tente novamente.", variant: "destructive" });
    } finally {
      setIsRedeeming(false);
    }
  };

  const applyPlanCoupon = async (coupon: any) => {
    const planType = coupon.plan_type || 'basic';
    const limits = getPlanLimits(planType);

    const endDate = new Date();
    endDate.setDate(endDate.getDate() + (coupon.plan_duration_days || 30));
    const startDateISO = new Date().toISOString();
    const endDateISO = endDate.toISOString();

    // 1. Desativar TODAS as assinaturas ativas do usuário
    const { error: deactivateErr } = await supabase
      .from("subscriptions")
      .update({ status: "inactive", updated_at: new Date().toISOString() })
      .eq("user_id", user!.id)
      .eq("status", "active");

    if (deactivateErr) throw new Error("Erro ao desativar assinaturas anteriores: " + deactivateErr.message);

    // 2. Criar nova assinatura completa
    const { error: insertErr } = await supabase.from("subscriptions").insert({
      user_id: user!.id,
      plan_type: planType,
      status: "active",
      storage_limit: limits.storage,
      credits_limit: limits.credits,
      credits_used: 0,
      page_limit: limits.pageLimit,
      conversation_retention_days: limits.retentionDays,
      start_date: startDateISO,
      subscription_start_date: startDateISO,
      end_date: endDateISO,
      next_billing_date: endDateISO,
    });

    if (insertErr) throw new Error("Erro ao criar assinatura: " + insertErr.message);

    // 3. Atualizar user_credits para o mês atual
    const currentMonth = new Date().toISOString().slice(0, 7);
    const { data: existingCredits } = await supabase
      .from("user_credits")
      .select("id")
      .eq("user_id", user!.id)
      .eq("month_year", currentMonth)
      .maybeSingle();

    if (existingCredits) {
      const { error: credErr } = await supabase
        .from("user_credits")
        .update({ limit_credits: limits.credits, updated_at: new Date().toISOString() })
        .eq("user_id", user!.id)
        .eq("month_year", currentMonth);
      if (credErr) console.error("Aviso: erro ao atualizar créditos:", credErr);
    } else {
      const { error: credErr } = await supabase.from("user_credits").insert({
        user_id: user!.id,
        credits_input: 0,
        credits_output: 0,
        total_credits: 0,
        limit_credits: limits.credits,
        month_year: currentMonth,
      });
      if (credErr) console.error("Aviso: erro ao criar créditos:", credErr);
    }
  };

  const applyCreditsCoupon = async (coupon: any) => {
    const { error } = await supabase.rpc("add_purchased_credits", {
      p_user_id: user!.id,
      p_credits_amount: coupon.credits_amount,
    });
    if (error) throw new Error("Erro ao adicionar créditos: " + error.message);
  };

  const applyStorageCoupon = async (coupon: any) => {
    const { data: purchase, error: purchaseErr } = await supabase.from("storage_purchases").insert({
      user_id: user!.id,
      package_type: "coupon",
      price_cents: 0,
      storage_amount: coupon.storage_amount,
      status: "completed",
    }).select("id").single();

    if (purchaseErr || !purchase) throw new Error("Erro ao criar compra de storage: " + (purchaseErr?.message || "sem dados"));

    const { error } = await supabase.rpc("add_purchased_storage", {
      p_user_id: user!.id,
      p_purchase_id: purchase.id,
      p_storage_amount: coupon.storage_amount,
    });
    if (error) throw new Error("Erro ao adicionar storage: " + error.message);
  };

  const formatCredits = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return `${n}`;
  };

  const formatBytes = (bytes: number) => {
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(0)} GB`;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
    return `${bytes} bytes`;
  };

  if (!user) return null;

  return (
    <div className="max-w-lg mx-auto mb-8">
      <div className="relative p-[2px] rounded-xl coupon-rgb-border">
        <div className="bg-gray-900 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Ticket className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-400 font-medium">Possui um cupom? Insira aqui</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Ex: JUS-ABCD1234"
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 font-mono tracking-wider"
              onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
              disabled={isRedeeming}
            />
            <Button
              onClick={handleRedeem}
              disabled={!code.trim() || isRedeeming}
              className={redeemed ? "bg-green-600 hover:bg-green-700" : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"}
            >
              {isRedeeming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : redeemed ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                "Resgatar"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
